import CampaignPlatformType from '@/Enums/CampaignManagement/CampaignPlatformType.enum';
import ScreenKey from '@/Enums/Screens/ScreenKey.enum';
import { ICreative } from '@/Interfaces/Creatives/Creative.interface';
import { IDV360TicketsCreative } from '@/Interfaces/DV360Tickets/Creative.interface';
import { DEFAULT_CREATIVE_FILTERS } from '@/constants/creativeLibrary';
import DV360CreativeFormModal from '@/pages/CreativeLibrary/Modals/DV360CreativeFormModal';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { selectIsDV360CampaignManagementEnabled } from '@/store/selectors/currentWhitelabel.selectors';
import { selectIsAdmin, selectMeScreenAccess } from '@/store/selectors/me.selectors';
import {
  archiveCreatives,
  deleteCreative,
  getCreatives,
  setFilters,
  updateCreative,
} from '@/store/slices/creativeSlice';
import { getClients } from '@/store/slices/sharedApi.slice';
import { CreativeStatus } from '@/types/creativeLibrary';
import { Paper } from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import BulkAssignLineItemsModal from './Modals/BulkAssignLineItemsModal';
import CreativeFormModal from './Modals/CreativeFormModal';
import Table from './Table';

const pluralizeCreatives = (count: number) => `${count} creative${count === 1 ? '' : 's'}`;

export const CreativeLibrary = () => {
  const dispatch = useAppDispatch();

  const creatives = useAppSelector(({ creative }) => creative.creatives);
  const loading = useAppSelector(({ creative }) => !!creative.creativesRequestId);
  const filters = useAppSelector(({ creative }) => creative.filters);
  const rowCount = useAppSelector(({ creative }) => creative.total);

  const isDV360CampaignManagementEnabled = useAppSelector(selectIsDV360CampaignManagementEnabled);
  const isAdmin = useAppSelector(selectIsAdmin);
  const readOnly = useAppSelector(selectMeScreenAccess(ScreenKey.CREATIVE_LIBRARY));
  const canArchive = isAdmin && !readOnly && filters.platform !== CampaignPlatformType.Google;

  const getCreativesData = useCallback(() => dispatch(getCreatives({ loadLineItems: true })), [dispatch]);

  useEffect(() => {
    dispatch(getClients());
    // Reset filters when the component mounts in case there are any filters applied from other pages
    dispatch(setFilters(DEFAULT_CREATIVE_FILTERS));
  }, [dispatch]);

  useEffect(() => {
    getCreativesData();
  }, [filters, getCreativesData]);

  const [isBeeswaxCreativeFormModalOpen, setIsBeeswaxCreativeFormModalOpen] = useState<boolean>(false);
  const [isDV360CreativeFormModalOpen, setIsDV360CreativeFormModalOpen] = useState<boolean>(false);
  const [creativeFormModalData, setCreativeFormModalData] = useState<ICreative | IDV360TicketsCreative>();
  const [bulkAssignCreative, setBulkAssignCreative] = useState<ICreative | null>(null);

  const handleCreativeFormModalClose = useCallback(
    (refresh: boolean = false) => {
      setIsBeeswaxCreativeFormModalOpen(false);
      setIsDV360CreativeFormModalOpen(false);
      setCreativeFormModalData(undefined);
      if (refresh === true) {
        getCreativesData();
      }
    },
    [getCreativesData],
  );

  const handleModalOpen = useCallback(
    (platform?: CampaignPlatformType) => {
      if (platform === CampaignPlatformType.Google && isDV360CampaignManagementEnabled) {
        setIsDV360CreativeFormModalOpen(true);
        return;
      }
      setIsBeeswaxCreativeFormModalOpen(true);
    },
    [isDV360CampaignManagementEnabled],
  );

  const handleAddCreative = useCallback(
    (platform: CampaignPlatformType) => {
      handleModalOpen(platform);
      setCreativeFormModalData(undefined);
    },
    [handleModalOpen],
  );

  const handleViewCreative = useCallback(
    (creative: ICreative | IDV360TicketsCreative) => {
      handleModalOpen(creative.platform);
      setCreativeFormModalData(creative);
    },
    [handleModalOpen],
  );

  const handleBulkAssign = useCallback((creative: ICreative) => {
    setIsBeeswaxCreativeFormModalOpen(false);
    setBulkAssignCreative(creative);
  }, []);

  const handleBulkAssignClose = useCallback(() => {
    setBulkAssignCreative(null);
    getCreativesData();
  }, [getCreativesData]);

  const handleChangeStatus = useCallback(
    async (id: number, status: CreativeStatus) => {
      try {
        await dispatch(updateCreative({ id, status })).unwrap();
      } catch (error) {
        toast.error(`Failed to update status: ${(error as Error)?.message}`);
      }
    },
    [dispatch],
  );

  const handleDeleteCreative = useCallback(
    async (creative: ICreative) => {
      try {
        await dispatch(deleteCreative(creative.id)).unwrap();
        toast.success('Creative deleted successfully');
        handleCreativeFormModalClose();
      } catch {
        toast.error('Failed to delete creative');
      }
    },
    [dispatch, handleCreativeFormModalClose],
  );

  const handleArchiveCreatives = useCallback(
    async (ids: number[], isArchived: boolean) => {
      const action = isArchived ? 'archive' : 'unarchive';
      try {
        const { updated, skipped } = await dispatch(archiveCreatives({ ids, isArchived })).unwrap();
        if (updated.length) {
          toast.success(`${pluralizeCreatives(updated.length)} ${action}d`);
          getCreativesData();
        }
        const inUseCount = skipped.filter(({ reason }) => reason === 'in_use').length;
        if (inUseCount) {
          toast.warning(`${pluralizeCreatives(inUseCount)} not archived because they are used by a line item`);
        }
        if (skipped.length > inUseCount) {
          toast.error(`Failed to ${action} ${pluralizeCreatives(skipped.length - inUseCount)}`);
        }
      } catch {
        toast.error(`Failed to ${action} creatives`);
      }
    },
    [dispatch, getCreativesData],
  );

  return (
    <Paper className='mx-7 mt-[30px] mb-8'>
      <Table
        loading={loading}
        tableData={creatives}
        rowCount={rowCount}
        handleAddCreative={handleAddCreative}
        handleViewCreative={handleViewCreative}
        handleChangeStatus={handleChangeStatus}
        handleArchive={handleArchiveCreatives}
        canArchive={canArchive}
        handleRefresh={getCreativesData}
      />

      <CreativeFormModal
        open={isBeeswaxCreativeFormModalOpen}
        handleClose={handleCreativeFormModalClose}
        creativeData={creativeFormModalData as ICreative}
        onBulkAssign={handleBulkAssign}
        onDelete={handleDeleteCreative}
      />

      {bulkAssignCreative && (
        <BulkAssignLineItemsModal
          open={!!bulkAssignCreative}
          handleClose={handleBulkAssignClose}
          creative={bulkAssignCreative}
        />
      )}

      {isDV360CampaignManagementEnabled && (
        <DV360CreativeFormModal
          open={isDV360CreativeFormModalOpen}
          handleClose={handleCreativeFormModalClose}
          creativeData={creativeFormModalData as IDV360TicketsCreative}
        />
      )}
    </Paper>
  );
};
